import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import {
  useClubAdminDeleteMemberMutation,
  useClubAdminGetClubMembersQuery,
  useClubAdminUpdateMemberMutation,
  useSendInvitesMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import ClubMembersTable from './ClubMembersTable.tsx'
import SimpleModalDialog from '../modals/SimpleModalDialog.tsx'
import ConfirmActionDialog from '../modals/ConfirmActionDialog.tsx'
import AddMemberDialog, {
  type AddMemberFormState,
  type RoleOption,
} from '../modals/AddMemberDialog.tsx'
import ImportMembersDialog from '../modals/ImportMembersDialog.tsx'
import { getApiErrorMessage } from '../../utils/ApiError.ts'

//TODO: Fetch roles from API when available
const ROLE_OPTIONS: RoleOption[] = [
  {
    id: 1,
    name: 'Admin',
    description: 'Full access to club management',
  },
  {
    id: 2,
    name: 'Member',
    description: 'Standard access for club members',
  },
  {
    id: 3,
    name: 'Guest',
    description: 'Guest access',
  },
]

interface ClubMembersHeaderProps {
  total: number
  onAddMember: () => void
  onImportMembers: () => void
}

const ClubMembersHeader = ({
  total,
  onAddMember,
  onImportMembers,
}: ClubMembersHeaderProps) => (
  <header className="mb-2 py-2 flex items-center justify-between gap-3">
    <h3 className="font-semibold text-gray-900">Club members ({total})</h3>

    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onImportMembers}
        className="inline-flex items-center justify-center gap-x-2 rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
        Import members
      </button>

      <button
        type="button"
        onClick={onAddMember}
        className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
        Add member
      </button>
    </div>
  </header>
)

const ClubMembers = () => {
  const { clubId = '' } = useParams()
  const { userLoaded } = useAppAuth()

  const {
    data: members,
    isLoading,
    isError,
    isSuccess,
  } = useClubAdminGetClubMembersQuery(clubId, {
    skip: !clubId || !userLoaded,
  })

  const [updateMember, { isSuccess: isUpdateSuccess, isError: isUpdateError }] =
    useClubAdminUpdateMemberMutation()
  const [deleteMember, { isSuccess: isDeleteSuccess, isError: isDeleteError }] =
    useClubAdminDeleteMemberMutation()
  const [sendInvites, { isLoading: isSendingInvite }] = useSendInvitesMutation()

  const [modalState, setModalState] = useState({
    isOpen: false,
    success: true,
    title: '',
    description: '',
    buttonText: 'Close',
  })

  const [lastAction, setLastAction] = useState<
    | {
        type: 'update'
        userName: string
        roleName: string
      }
    | {
        type: 'delete'
        userName: string
      }
    | null
  >(null)

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    userId: string
    userName: string
  } | null>(null)

  const [resendConfirmation, setResendConfirmation] = useState<{
    name: string
    email: string
    roleId: number
  } | null>(null)

  const defaultRoleId =
    ROLE_OPTIONS.find((role) => role.name === 'Member')?.id ??
    ROLE_OPTIONS[0].id
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false)
  const [addMemberForm, setAddMemberForm] = useState<AddMemberFormState>({
    name: '',
    email: '',
    roleId: defaultRoleId,
  })
  const [addMemberError, setAddMemberError] = useState<string | null>(null)
  const [isImportMembersDialogOpen, setIsImportMembersDialogOpen] =
    useState(false)

  const openModal = ({
    success,
    title,
    description,
    buttonText,
  }: {
    success: boolean
    title: string
    description: string
    buttonText?: string
  }) => {
    setModalState({
      isOpen: true,
      success,
      title,
      description,
      buttonText: buttonText ?? 'Close',
    })
  }

  const closeModal = () => {
    setModalState((previous) => ({ ...previous, isOpen: false }))
  }

  useEffect(() => {
    if (isUpdateSuccess) {
      const userName =
        lastAction?.type === 'update' ? lastAction.userName : 'Member'
      const roleName =
        lastAction?.type === 'update'
          ? lastAction.roleName
          : 'the selected role'

      openModal({
        success: true,
        title: 'Member updated',
        description: `${userName} role has been changed to ${roleName}`,
      })
    }
  }, [isUpdateSuccess, lastAction])

  useEffect(() => {
    if (isDeleteSuccess) {
      const userName =
        lastAction?.type === 'delete' ? lastAction.userName : 'This member'

      openModal({
        success: true,
        title: 'Member removed',
        description: `Member ${userName} was removed successfully`,
      })
    }
  }, [isDeleteSuccess, lastAction])

  useEffect(() => {
    if (isUpdateError) {
      const userName =
        lastAction?.type === 'update' ? lastAction.userName : 'this member'

      openModal({
        success: false,
        title: 'Update failed',
        description: `Unable to update ${userName} role. Try again later`,
      })
    }
  }, [isUpdateError, lastAction])

  useEffect(() => {
    if (isDeleteError) {
      const userName =
        lastAction?.type === 'delete' ? lastAction.userName : 'this member'

      openModal({
        success: false,
        title: 'Removal failed',
        description: `Unable to remove ${userName}. Try again later`,
      })
    }
  }, [isDeleteError, lastAction])

  const handleAddMember = () => {
    setLastAction(null)
    setAddMemberForm({ name: '', email: '', roleId: defaultRoleId })
    setAddMemberError(null)
    setIsAddMemberDialogOpen(true)
  }

  const handleRoleChange = ({
    userId,
    userName,
    roleId,
    roleName,
  }: {
    userId: string | number | undefined
    userName: string
    roleId: number
    roleName: string
  }) => {
    if (userId == null) {
      return
    }
    setLastAction({ type: 'update', userName, roleName })
    updateMember({ clubId, userId: String(userId), body: { roleId } })
  }

  const handleRemoveMember = ({
    userId,
    userName,
  }: {
    userId: string | number | undefined
    userName: string
  }) => {
    setDeleteConfirmation({ userId: String(userId), userName })
  }

  const confirmRemoveMember = () => {
    if (!deleteConfirmation) {
      return
    }

    setLastAction({ type: 'delete', userName: deleteConfirmation.userName })
    deleteMember({ clubId, userId: deleteConfirmation.userId })
    setDeleteConfirmation(null)
  }

  const cancelRemoveMember = () => {
    setDeleteConfirmation(null)
    if (lastAction?.type === 'delete') {
      setLastAction(null)
    }
  }

  const closeAddMemberDialog = () => {
    setIsAddMemberDialogOpen(false)
    setAddMemberError(null)
  }

  const handleAddMemberNameChange = (value: string) => {
    setAddMemberForm((previous) => ({ ...previous, name: value }))
  }

  const handleAddMemberEmailChange = (value: string) => {
    setAddMemberForm((previous) => ({ ...previous, email: value }))
  }

  const handleAddMemberRoleChange = (value: number) => {
    setAddMemberForm((previous) => ({ ...previous, roleId: value }))
  }

  const resolveInviteErrorMessage = (error: unknown): string =>
    getApiErrorMessage(error, 'Unable to send invitation. Try again later')

  const submitAddMemberInvite = async () => {
    const trimmedName = addMemberForm.name.trim()
    const trimmedEmail = addMemberForm.email.trim()

    if (!trimmedName || !trimmedEmail) {
      setAddMemberError('Name and email are required')
      return
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(trimmedEmail)) {
      setAddMemberError('Enter a valid email address')
      return
    }

    if (!clubId) {
      setAddMemberError(
        'Club context is missing. Please reload the page and try again'
      )
      return
    }

    setAddMemberError(null)

    try {
      await sendInvites({
        clubId,
        body: {
          invites: [
            {
              name: trimmedName,
              email: trimmedEmail,
              clubAccessRoleId: addMemberForm.roleId,
            },
          ],
        },
      }).unwrap()

      setIsAddMemberDialogOpen(false)
      setAddMemberForm({ name: '', email: '', roleId: defaultRoleId })

      openModal({
        success: true,
        title: 'Invitation sent',
        description: `Invitation sent to ${trimmedName || trimmedEmail}`,
        buttonText: 'Close',
      })
    } catch (error) {
      console.error('Failed to send club invite', error)
      setAddMemberError(resolveInviteErrorMessage(error))
    }
  }

  const handleResendInvite = async ({
    name,
    email,
    roleId,
  }: {
    name: string
    email: string
    roleId: number
  }) => {
    if (!clubId) {
      openModal({
        success: false,
        title: 'Unable to resend invite',
        description:
          'Club context is missing. Please reload the page and try again',
      })
      return
    }

    const trimmedName = name.trim()
    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
      openModal({
        success: false,
        title: 'Unable to resend invite',
        description: 'Invite email is missing. Try inviting this member again',
      })
      return
    }

    try {
      await sendInvites({
        clubId,
        body: {
          invites: [
            {
              name: trimmedName || trimmedEmail,
              email: trimmedEmail,
              clubAccessRoleId: roleId,
            },
          ],
        },
      }).unwrap()

      openModal({
        success: true,
        title: 'Invitation re-sent',
        description: `Invitation sent to ${trimmedName || trimmedEmail}`,
      })
    } catch (error) {
      console.error('Failed to resend club invite', error)
      openModal({
        success: false,
        title: 'Unable to resend invite',
        description: resolveInviteErrorMessage(error),
      })
    }
  }

  const confirmResendInvite = () => {
    if (!resendConfirmation) {
      return
    }
    void handleResendInvite(resendConfirmation)
    setResendConfirmation(null)
  }

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <LoadingSpinner message="Loading members..." />
      </div>
    )
  }

  if (isError) {
    return <p className="text-red-600">Unable to load club members right now</p>
  }

  return (
    <div className="py-4 border-b">
      <ClubMembersHeader
        total={members?.length ?? 0}
        onAddMember={handleAddMember}
        onImportMembers={() => setIsImportMembersDialogOpen(true)}
      />

      {isSuccess && members && members.length > 0 && (
        <div className="overflow-x-auto">
          <ClubMembersTable
            members={members}
            roles={ROLE_OPTIONS}
            onRoleChange={handleRoleChange}
            onRemove={handleRemoveMember}
            onResendInvite={setResendConfirmation}
          />
        </div>
      )}

      {isSuccess && (!members || members.length === 0) && (
        <p className="text-sm text-gray-600">
          There are no members yet. Use the Add member button to invite someone
          to your club.
        </p>
      )}

      <AddMemberDialog
        isOpen={isAddMemberDialogOpen}
        onClose={closeAddMemberDialog}
        formState={addMemberForm}
        onNameChange={handleAddMemberNameChange}
        onEmailChange={handleAddMemberEmailChange}
        onRoleChange={handleAddMemberRoleChange}
        onSubmit={submitAddMemberInvite}
        isSubmitting={isSendingInvite}
        roles={ROLE_OPTIONS}
        error={addMemberError}
      />

      <ImportMembersDialog
        isOpen={isImportMembersDialogOpen}
        onClose={() => setIsImportMembersDialogOpen(false)}
        clubId={clubId}
      />

      <SimpleModalDialog
        isOpen={modalState.isOpen}
        onClose={closeModal}
        success={modalState.success}
        title={modalState.title}
        description={modalState.description}
        buttonText={modalState.buttonText}
      />

      <ConfirmActionDialog
        isOpen={Boolean(deleteConfirmation)}
        title="Remove member?"
        description={
          deleteConfirmation
            ? `Are you sure you want to remove ${deleteConfirmation.userName} from this club?`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveMember}
        onCancel={cancelRemoveMember}
      />

      <ConfirmActionDialog
        isOpen={Boolean(resendConfirmation)}
        title="Resend invite?"
        description={
          resendConfirmation
            ? `Send the invitation email to ${resendConfirmation.name || resendConfirmation.email} again?`
            : ''
        }
        confirmText="Resend"
        cancelText="Cancel"
        onConfirm={confirmResendInvite}
        onCancel={() => setResendConfirmation(null)}
      />
    </div>
  )
}

export default ClubMembers
